import { useCallback, useEffect, useRef, useState } from "react";
import {
  getAgentOptionsApi,
  getBranchOptionsApi,
  getProjectOptionsApi,
  getSaleAreaOptionsApi,
  getSaleBlockOptionsApi,
  getSupportDepartmentOptionsApi,
  normalizeHasNextPage,
  normalizeOptionItems,
} from "../api/filters.api";
import {
  getBusinessStaffListApi,
  getSupportBusinessStaffListApi,
} from "../api/staffManager.api";

export const OPTION_LIMIT = 10;

export const ALL_OPTION = { _id: "all", code: "ALL", name: "Tất cả" };

export const validId = (v) => (!v?._id || v._id === "all" ? undefined : v._id);

const FETCHERS = {
  AREA: getSaleAreaOptionsApi,
  BLOCK: getSaleBlockOptionsApi,
  SUPPORT_DEPARTMENT: getSupportDepartmentOptionsApi,
  BRANCH: getBranchOptionsApi,
  PROJECT: getProjectOptionsApi,
  AGENT: getAgentOptionsApi,
  EMPLOYEE: getBusinessStaffListApi,
  EMPLOYEE_SUPPORT: getSupportBusinessStaffListApi,
};

const uniqueById = (list) => {
  const map = new Map();
  list.forEach((it) => {
    if (it?._id && !map.has(it._id)) map.set(it._id, it);
  });
  return Array.from(map.values());
};

/**
 * Nạp danh mục cho bottom sheet lọc (phân trang + tìm kiếm), khớp hành vi app.
 * @param {string|null} sheetType AREA | BLOCK | SUPPORT_DEPARTMENT | BRANCH
 * @param {string} keyword từ khoá tìm kiếm trong sheet
 * @param {object} extraParams tham số phụ (vd: saleAreaId khi lọc khối)
 */
export const useFilterOptions = (sheetType, keyword, extraParams) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const pageRef = useRef(1);
  const reqRef = useRef(0);
  const extraKey = JSON.stringify(extraParams || {});

  const load = useCallback(
    async (page, mode) => {
      const fetcher = FETCHERS[sheetType];
      if (!fetcher) return;
      reqRef.current += 1;
      const rid = reqRef.current;
      if (mode === "append") setLoadingMore(true);
      else setLoading(true);
      try {
        const res = await fetcher({
          page,
          limit: OPTION_LIMIT,
          search: keyword || undefined,
          ...(extraParams || {}),
        });
        if (rid !== reqRef.current) return;
        const list = normalizeOptionItems(res);
        pageRef.current = page;
        setItems((prev) => (mode === "append" ? uniqueById([...prev, ...list]) : list));
        setHasMore(normalizeHasNextPage(res, list.length >= OPTION_LIMIT));
      } catch {
        if (rid === reqRef.current && mode !== "append") setItems([]);
      } finally {
        if (rid === reqRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sheetType, keyword, extraKey]
  );

  useEffect(() => {
    if (!FETCHERS[sheetType]) {
      setItems([]);
      setHasMore(false);
      return;
    }
    pageRef.current = 1;
    load(1, "replace");
  }, [sheetType, keyword, extraKey, load]);

  const loadMore = useCallback(() => {
    if (loading || loadingMore || !hasMore) return;
    load(pageRef.current + 1, "append");
  }, [loading, loadingMore, hasMore, load]);

  return { items, loading, loadingMore, hasMore, loadMore };
};

/** Ghép "Tất cả" + item đang chọn + danh sách tải về thành data cho SelectSheet. */
export const buildOptionSheetData = (options, selected, allLabel = "Tất cả") => {
  const all = { ...ALL_OPTION, name: allLabel };
  const picked = selected?._id && selected._id !== "all" ? [selected] : [];
  return uniqueById([all, ...picked, ...options]).map((x) => ({
    key: x._id,
    label: x.name || x.fullName || "-",
    raw: x,
  }));
};

/** Nhân viên: hiển thị "Họ tên (mã)" như app. */
export const buildEmployeeSheetData = (options, selected) => {
  const all = { ...ALL_OPTION, fullName: "Tất cả", name: "Tất cả" };
  const picked = selected?._id && selected._id !== "all" ? [selected] : [];
  return uniqueById([all, ...picked, ...options]).map((x) => ({
    key: x._id,
    label: x._id === "all" ? "Tất cả" : `${x.fullName || "-"}${x.code ? ` (${x.code})` : ""}`,
    raw: x,
  }));
};
